import { applyTraceparent } from '@myosh/tracing';
import { apiBaseUrl, apiKey } from '../env';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, unknown>;
}

/**
 * `transport` — the fetch rejected, so the answer is unknown, not negative.
 * `http` — a response arrived with a non-2xx status.
 */
export type ApiFailureKind = 'transport' | 'http';

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  /** Only set for `http` failures. */
  readonly status?: number;

  constructor(message: string, kind: ApiFailureKind, options: { status?: number; cause?: unknown } = {}) {
    super(message, { cause: options.cause });
    this.name = 'ApiError';
    this.kind = kind;
    this.status = options.status;
  }
}

/** Cap on the response-body excerpt kept as `cause`, matching `@myosh/sentry`. */
const MAX_DIAGNOSTIC_LEN = 500;

const api = async <T>(endpoint: string, opts: ApiOptions = {}) => {
  const { body, ...rest } = opts;

  const url = `${apiBaseUrl}v4/${endpoint}`;

  const config: RequestInit = {
    method: 'GET',
    ...rest,
    headers: {
      'content-type': 'application/json',
      'x-api-key': apiKey,
      ...rest.headers,
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  };

  // W3C trace context on the shared auth/cluster/schema fetch (allow-list gated;
  // no-op when apiBaseUrl is unset, e.g. the mobile WebView build).
  applyTraceparent(config.headers as Record<string, unknown>, url, { allowedBaseUrl: apiBaseUrl });

  let response: Response;
  try {
    response = await fetch(url, config);
  } catch (cause) {
    // Constant message (no url) so Sentry groups transport failures together.
    throw new ApiError('Network error. The request did not reach the API.', 'transport', { cause });
  }

  if (!response.ok) {
    // A body read can itself reject; it must not escape as a non-`ApiError`.
    let bodyExcerpt: Error | undefined;
    try {
      const errorMessage = await response.text();
      // Status only in the message: a body can carry PII and would shatter
      // Sentry grouping. A bounded excerpt rides on `cause` instead.
      bodyExcerpt = errorMessage ? new Error(errorMessage.slice(0, MAX_DIAGNOSTIC_LEN)) : undefined;
    } catch (readError) {
      bodyExcerpt = readError instanceof Error ? readError : new Error(String(readError));
    }

    throw new ApiError(`HTTP error. Status: ${response.status}`, 'http', {
      status: response.status,
      cause: bodyExcerpt,
    });
  }

  const data: T = await response.json();
  return data;
};

export default api;
